import { useZero } from "@rocicorp/zero/react";
import { styled } from "goober";
import { useState } from "react";
import { spaceEdgeHostnameSuffix } from "../../shared/edgeUrl";
import { sharedMutators } from "../../shared/mutators";
import { MAX_SLUG_LENGTH } from "../../shared/slug";
import { useSessionTenantEdgeUrlOptions } from "../hooks/useSessionTenantOrigin";
import { useSlugField } from "../hooks/useSlugField";
import { useSpaces } from "../hooks/useSpaces";
import { useTenantCustomDomain } from "../hooks/useTenantCustomDomain";
import { Box } from "./Box";
import { ConfirmModal } from "./ConfirmModal";
import { CopyableValue } from "./Settings/CopyableValue";
import { Text } from "./Text";
import { fieldOutline } from "./fieldOutline";

const SlugInputBox = styled("label")`
  display: flex;
  align-items: stretch;
  background-color: var(--surface-primary);
  ${fieldOutline("&:focus-within")}
  border-radius: 8px;
  height: 32px;
  cursor: text;
  overflow: hidden;
`;

const SlugInputField = styled("input")`
  flex: 1;
  min-width: 0;
  padding: 8px 12px;
  background: transparent;
  border: 0;
  outline: 0;
  color: var(--text-primary);
  font-size: 12px;
  font-family: InterVariable;
  line-height: 16px;

  &::placeholder {
    opacity: 0.3;
  }

  &:focus::placeholder {
    opacity: 0.6;
  }
`;

const SlugSuffix = styled("span")`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  padding: 0 12px;
  background-color: var(--surface-secondary);
  border-left: 0.5px solid var(--border-alpha);
  color: var(--text-secondary);
  font-size: 12px;
  font-family: InterVariable;
  line-height: 16px;
  white-space: nowrap;
`;

function useSpaceHostSuffix(): string | null {
  const customDomain = useTenantCustomDomain();
  const edgeUrlOptions = useSessionTenantEdgeUrlOptions();
  return spaceEdgeHostnameSuffix(edgeUrlOptions, customDomain);
}

export function SpaceSlugInput(
  inputProps: React.InputHTMLAttributes<HTMLInputElement>
) {
  const hostSuffix = useSpaceHostSuffix();
  return (
    <SlugInputBox>
      <SlugInputField {...inputProps} maxLength={MAX_SLUG_LENGTH} />
      {hostSuffix !== null && <SlugSuffix>{hostSuffix}</SlugSuffix>}
    </SlugInputBox>
  );
}

export function SpaceUrlField({
  space,
  canManageSpace,
}: {
  space: { id: string; slug: string; customHostname: string | null };
  canManageSpace: boolean;
}) {
  const z = useZero();
  const [allSpaces] = useSpaces({ settingsView: false });
  const hostSuffix = useSpaceHostSuffix();
  const [pendingSlug, setPendingSlug] = useState<string | null>(null);
  const slugField = useSlugField({
    currentSlug: space.slug,
    allItems: allSpaces,
    currentId: space.id,
    onCommit: setPendingSlug,
  });
  const confirmSlugChange = () => {
    if (pendingSlug !== null) {
      z.mutate(
        sharedMutators.updateSpace({ spaceId: space.id, slug: pendingSlug })
      );
    }
    setPendingSlug(null);
  };

  return (
    <Box gap={10}>
      <Box horizontal={8}>
        <Text variant="text-small-md">Space URL</Text>
      </Box>
      {space.customHostname ? (
        <CopyableValue value={`https://${space.customHostname}/`}>
          <Text variant="code-small">{space.customHostname}</Text>
        </CopyableValue>
      ) : (
        <SpaceSlugInput
          aria-label="Space URL"
          value={slugField.value}
          onChange={(e) => slugField.setValue(e.target.value)}
          onBlur={slugField.commit}
          onKeyDown={(e) => {
            if (e.key === "Enter") e.currentTarget.blur();
          }}
          disabled={!canManageSpace}
        />
      )}
      <Box horizontal={8}>
        {space.customHostname ? (
          <Text variant="text-small" color="var(--text-secondary)">
            Your workflows’ links use this custom hostname.
          </Text>
        ) : slugField.error ? (
          <Text variant="text-small" color="var(--text-danger)">
            {slugField.error}
          </Text>
        ) : slugField.slugTaken ? (
          <Text variant="text-small" color="var(--text-danger)">
            This space URL is already taken.
          </Text>
        ) : (
          <Text variant="text-small" color="var(--text-secondary)">
            Your workflows’ links live at this address, so changing it breaks
            any that are already in use.
          </Text>
        )}
      </Box>
      <ConfirmModal
        open={pendingSlug !== null}
        onClose={() => setPendingSlug(null)}
        onConfirm={confirmSlugChange}
        title="Change the space URL?"
        description={
          hostSuffix
            ? `The space will move to ${pendingSlug ?? ""}${hostSuffix}. Existing links and email addresses at ${space.slug}${hostSuffix} will stop working.`
            : "Existing links to this space and its email addresses will stop working."
        }
        confirmLabel="Change URL"
        destructive
      />
    </Box>
  );
}
